import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Box, Chip, Container, Paper, Stack, Typography } from "@mui/material";
import LoadingComponent from "../utils/LoadingComponent";

// «مراجعة إجاباتي» — the student sees every question, what they answered, the
// correct answer, and the mark they earned.
//
// Note on marks: a multiple-choice answer carries no stored score (it is graded
// from the correct option), so the mark is derived here the same way the total
// is derived on the server.
const API = process.env.REACT_APP_API_URL;

const ReviewExamDetails = () => {
  const { examId } = useParams();
  const currentUser = useSelector((state) => state.user).currentUser;

  const [rows, setRows] = useState([]);
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const totalRef = useRef(0);

  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${currentUser?.token}`,
      Accept: "application/json",
    };

    const fetchPage = (paginate) =>
      axios
        .post(`${API}/exam/get-exam-questions?page=1`, { exam_id: examId, paginate }, { headers })
        .then((response) => {
          totalRef.current = response?.data?.data?.total ?? 0;
          const list = response?.data?.data?.data;
          if (Array.isArray(list)) setRows(list);
          return totalRef.current;
        });

    // one call learns the count, the second asks for all of them
    fetchPage(0)
      .then((total) => (total > 0 ? fetchPage(total) : null))
      .catch((err) =>
        setError(err?.response?.data?.message || "تعذّر تحميل إجاباتك، حاول مرة أخرى.")
      )
      .finally(() => setLoading(false));

    axios
      .get(`${API}/exam/get-exam/${examId}`, { headers })
      .then((response) => setExam(response?.data?.data))
      .catch(() => { });
  }, [examId, currentUser]);

  if (loading) return <LoadingComponent />;

  if (error) {
    return (
      <Container maxWidth="md" sx={{ paddingTop: "100px", paddingBottom: "60px" }}>
        <Typography variant="h6" color="error" textAlign="center">{error}</Typography>
      </Container>
    );
  }

  if (rows.length === 0) {
    return (
      <Container maxWidth="md" sx={{ paddingTop: "100px", paddingBottom: "60px" }}>
        <Typography variant="h6" color="GrayText" textAlign="center">
          لا توجد إجابات لعرضها في هذا الامتحان.
        </Typography>
      </Container>
    );
  }

  const graded = rows.map((row) => {
    const question = row.questions?.[0] ?? {};
    const options = question.options ?? [];
    const correct = options.find((option) => option.is_correct);
    const isChoose = question.type === "choose";
    const answered = row.current_answer;

    const isCorrect = isChoose ? Boolean(correct) && answered === correct.title : null;
    const max = Number(question.score) || 0;
    // teacher-graded types keep whatever the teacher entered
    const earned = isChoose ? (isCorrect ? max : 0) : Number(row.score) || 0;

    return { row, question, options, correct, isChoose, answered, isCorrect, max, earned };
  });

  const totalMax = graded.reduce((sum, item) => sum + item.max, 0);
  const totalEarned = graded.reduce((sum, item) => sum + item.earned, 0);
  const rightCount = graded.filter((item) => item.isCorrect === true).length;
  const wrongCount = graded.filter((item) => item.isCorrect === false).length;
  const pending = graded.filter((item) => item.isCorrect === null).length;

  const percent = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0;
  const tone = percent >= 80 ? "#2e7d32" : percent >= 50 ? "#f6a609" : "#d32f2f";

  return (
    <Box sx={{ background: "#F8F8FA", minHeight: "70vh", paddingBottom: "60px" }}>
      <Container maxWidth="md" sx={{ paddingTop: "100px" }}>
        {/* the result, right at the top */}
        <Paper elevation={2} sx={{ borderRadius: "20px", padding: "26px", textAlign: "center", marginBottom: "22px" }}>
          <Typography variant="h6" color="text.secondary">{exam?.title || "نتيجتي"}</Typography>
          <Typography sx={{ fontSize: "2.6rem", fontWeight: 800, color: tone, lineHeight: 1.2 }}>
            {totalEarned} <span style={{ fontSize: "1.2rem", color: "#6b7a89" }}>/ {totalMax}</span>
          </Typography>
          <Typography sx={{ color: tone, fontWeight: 700 }}>{percent}%</Typography>

          <Stack direction="row" sx={{ gap: 1, justifyContent: "center", flexWrap: "wrap", marginTop: 2 }}>
            <Chip color="success" label={`✅ صحيحة: ${rightCount}`} sx={{ fontWeight: 700 }} />
            <Chip color="error" label={`❌ خاطئة: ${wrongCount}`} sx={{ fontWeight: 700 }} />
            {pending > 0 && (
              <Chip color="warning" label={`⏳ بانتظار تصحيح المعلم: ${pending}`} sx={{ fontWeight: 700 }} />
            )}
          </Stack>
        </Paper>

        <Stack sx={{ gap: 2 }}>
          {graded.map((item, index) => {
            const border =
              item.isCorrect === true ? "#2e7d32" : item.isCorrect === false ? "#d32f2f" : "#f6a609";

            return (
              <Paper
                key={item.row.id}
                elevation={1}
                sx={{ borderRadius: "16px", padding: "20px", borderInlineStart: `5px solid ${border}` }}
              >
                <Stack direction="row" sx={{ alignItems: "flex-start", gap: 1, flexWrap: "wrap", marginBottom: 1 }}>
                  <Typography sx={{ fontWeight: 800, color: "#6b7a89" }}>{index + 1}.</Typography>
                  <Typography sx={{ flex: 1, fontWeight: 700, whiteSpace: "pre-line", minWidth: 180 }}>
                    {item.question.title}
                  </Typography>
                  <Chip
                    size="small"
                    sx={{ fontWeight: 800, background: border, color: "#fff" }}
                    label={`${item.earned} / ${item.max}`}
                  />
                </Stack>

                {item.isChoose ? (
                  <Stack sx={{ gap: 0.8, marginTop: 1.5 }}>
                    {item.options.map((option, i) => {
                      const chosen = option.title === item.answered;
                      const right = Boolean(option.is_correct);
                      const background = right ? "#e8f5e9" : chosen ? "#ffebee" : "#F8F8FA";
                      const color = right ? "#2e7d32" : chosen ? "#c62828" : "#46596b";

                      return (
                        <Box
                          key={i}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
                            background, color, borderRadius: "10px", padding: "10px 14px",
                            fontWeight: right || chosen ? 700 : 500,
                          }}
                        >
                          <span>{right ? "✅" : chosen ? "❌" : "•"}</span>
                          <Typography sx={{ flex: 1, fontSize: "0.92rem" }}>{option.title}</Typography>
                          {chosen && (
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 800 }}>إجابتك</Typography>
                          )}
                          {right && !chosen && (
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 800 }}>الإجابة الصحيحة</Typography>
                          )}
                        </Box>
                      );
                    })}

                    {!item.answered && (
                      <Typography sx={{ fontSize: "0.82rem", color: "#c62828" }}>لم تُجب على هذا السؤال.</Typography>
                    )}
                  </Stack>
                ) : (
                  <Box sx={{ marginTop: 1.5 }}>
                    <Typography sx={{ fontSize: "0.8rem", color: "#6b7a89" }}>إجابتك:</Typography>
                    <Typography sx={{ background: "#F8F8FA", borderRadius: "10px", padding: "10px 14px", marginTop: 0.5 }}>
                      {item.answered || "—"}
                    </Typography>
                    {item.isCorrect === null && (
                      <Typography sx={{ fontSize: "0.78rem", color: "#8a5b06", marginTop: 0.8 }}>
                        ⏳ هذا السؤال يصححه المعلم.
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
};

export default ReviewExamDetails;
